import React, { ReactNode } from 'react';
import { t } from '@lingui/core/macro';
import { Skeleton } from 'src/components/ui';
import Notification from 'src/components/Notification';
import EditorToolbar from './EditorToolbar';
import { FormLoadingProvider } from './FormLoading';

export type EditorPageProps = {
  breadcrumb?: ReactNode;
  title: ReactNode;
  isDirty: boolean;
  lastSavedAt: number | null;
  loading?: boolean;
  /**
   * While `loading`, render the page's own sections with placeholders in their controls (see
   * useFormLoading) instead of generic skeleton cards. The page must render its sections without data.
   */
  inlineLoading?: boolean;
  error?: string | Error | null;
  errorCount?: number;
  showErrorSummary?: boolean;
  actions?: ReactNode;
  onCancel?: () => void;
  onSave?: () => void;
  saving?: boolean;
  saveLabel?: ReactNode;
  /** Keep Save enabled while nothing changed, e.g. "Publish" on a saved draft. */
  allowSaveWhenClean?: boolean;
  children: ReactNode;
};

/** Page frame shared by the record editors, laid out like the builders: toolbar on top, a scrolling body with the banners. */
export function EditorPage({
  breadcrumb,
  title,
  isDirty,
  lastSavedAt,
  loading,
  inlineLoading,
  error,
  errorCount = 0,
  showErrorSummary,
  actions,
  onCancel,
  onSave,
  saving,
  saveLabel,
  allowSaveWhenClean,
  children,
}: EditorPageProps) {
  const errorObject = typeof error === 'string' ? new Error(error) : error;

  return (
    <div className="flex flex-col h-full min-h-0">
      <EditorToolbar
        breadcrumb={breadcrumb}
        title={title}
        isDirty={isDirty}
        lastSavedAt={lastSavedAt}
        actions={actions}
        onCancel={onCancel}
        onSave={onSave}
        saving={saving}
        saveLabel={saveLabel}
        allowSaveWhenClean={allowSaveWhenClean}
        loading={!!loading}
      />

      <div className="flex-1 min-h-0 overflow-auto p-4">
        {errorObject ? <Notification error={errorObject} /> : null}
        {showErrorSummary && errorCount > 0 ? <Notification color="danger">{t`Fix ${errorCount} field(s) before saving.`}</Notification> : null}
        {loading && !inlineLoading ? (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
            <div className="lg:col-span-7">
              <Skeleton height={180} />
            </div>
            <div className="lg:col-span-5">
              <Skeleton height={180} />
            </div>
            <div className="lg:col-span-12">
              <Skeleton height={140} />
            </div>
          </div>
        ) : (
          <FormLoadingProvider loading={!!loading}>{children}</FormLoadingProvider>
        )}
      </div>
    </div>
  );
}

export default EditorPage;
